import { reactive, ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';

// These are the exact values seeded for the `stage` custom attribute
// (lib/tasks/contacts_plus_attributes.rake) — the Kanban board is a read
// model over that existing attribute, not a new data source.
export const STAGES = [
  'New',
  'Contacted',
  'Qualified',
  'Meeting Scheduled',
  'Proposal Sent',
  'Negotiation',
  'Won',
  'Lost',
];

export const VERIFICATION_STATUSES = ['Pending Review', 'Verified', 'Junk'];
export const PRIORITIES = ['urgent', 'high', 'medium', 'low'];

const PER_PAGE = 15;

export function useKanbanBoard() {
  const store = useStore();
  const { t } = useI18n();

  const columns = reactive(
    STAGES.reduce((acc, stage) => {
      acc[stage] = {
        contacts: [],
        page: 1,
        hasMore: false,
        count: 0,
        isLoading: false,
      };
      return acc;
    }, {})
  );

  const filters = reactive({
    verificationStatus: '',
    company: '',
    agent: '',
    priority: '',
    channel: '',
  });
  const searchTerm = ref('');
  const sortBy = ref('newest');

  const searchResultsByStage = ref(null); // null when not searching

  const sortAttrForServer = computed(() => {
    if (sortBy.value === 'oldest') return 'created_at';
    return '-created_at';
  });

  const buildServerConditions = stage => {
    const conditions = [
      {
        attribute_key: 'stage',
        attribute_model: 'contact_attribute',
        filter_operator: 'equal_to',
        values: [stage],
        query_operator: 'and',
      },
    ];

    if (filters.verificationStatus) {
      conditions.push({
        attribute_key: 'verification_status',
        attribute_model: 'contact_attribute',
        filter_operator: 'equal_to',
        values: [filters.verificationStatus],
        query_operator: 'and',
      });
    }

    if (filters.company) {
      conditions.push({
        attribute_key: 'company_name',
        attribute_model: 'standard',
        filter_operator: 'contains',
        values: [filters.company],
        query_operator: 'and',
      });
    }

    conditions[conditions.length - 1].query_operator = undefined;
    return { payload: conditions };
  };

  const passesClientFilters = contact => {
    if (filters.agent && contact.assignee?.id !== Number(filters.agent)) {
      return false;
    }
    if (filters.priority && contact.priority !== filters.priority) {
      return false;
    }
    if (filters.channel && contact.source_channel !== filters.channel) {
      return false;
    }
    return true;
  };

  const SORT_COMPARATORS = {
    priority: (a, b) =>
      PRIORITIES.indexOf(a.priority || 'zzz') -
      PRIORITIES.indexOf(b.priority || 'zzz'),
    next_follow_up: (a, b) => {
      const aVal = a.custom_attributes?.next_follow_up_at || '';
      const bVal = b.custom_attributes?.next_follow_up_at || '';
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      return new Date(aVal) - new Date(bVal);
    },
    last_conversation: (a, b) =>
      (b.last_conversation_at || 0) - (a.last_conversation_at || 0),
  };

  const applyClientSort = contactsList => {
    const comparator = SORT_COMPARATORS[sortBy.value];
    if (!comparator) return contactsList;
    return [...contactsList].sort(comparator);
  };

  const cardsForStage = stage => {
    const source = searchResultsByStage.value
      ? searchResultsByStage.value[stage] || []
      : columns[stage].contacts;
    return applyClientSort(source.filter(passesClientFilters));
  };

  const fetchColumn = async (stage, { append = false } = {}) => {
    const column = columns[stage];
    const page = append ? column.page + 1 : 1;
    column.isLoading = true;
    try {
      const { data } = await ContactAPI.filter(
        page,
        sortAttrForServer.value,
        buildServerConditions(stage)
      );
      column.contacts = append
        ? [...column.contacts, ...data.payload]
        : data.payload;
      column.page = page;
      column.count = data.meta?.count ?? data.payload.length;
      column.hasMore = data.payload.length === PER_PAGE;
    } catch (error) {
      useAlert(t('KANBAN.ERRORS.LOAD_FAILED'));
    } finally {
      column.isLoading = false;
    }
  };

  const fetchAllColumns = async () => {
    searchResultsByStage.value = null;
    await Promise.all(STAGES.map(stage => fetchColumn(stage)));
  };

  const loadMore = stage => fetchColumn(stage, { append: true });

  const runSearch = async term => {
    searchTerm.value = term;
    if (!term) {
      searchResultsByStage.value = null;
      return;
    }
    try {
      const { data } = await ContactAPI.search(term, 1, 'name');
      const grouped = STAGES.reduce((acc, stage) => {
        acc[stage] = [];
        return acc;
      }, {});
      data.payload.forEach(contact => {
        const stage = contact.custom_attributes?.stage;
        if (stage && grouped[stage]) grouped[stage].push(contact);
      });
      searchResultsByStage.value = grouped;
    } catch (error) {
      useAlert(t('KANBAN.ERRORS.SEARCH_FAILED'));
    }
  };

  const moveCard = async (contact, fromStage, toStage) => {
    if (fromStage === toStage) return;

    // vuedraggable already spliced `contact` out of `columns[fromStage]
    // .contacts` and into `columns[toStage].contacts` as part of the drag
    // gesture itself (both columns are bound via `:list` in the same drag
    // group, which vuedraggable mutates directly) — only the persisted
    // stage value on the object still needs updating, not the list
    // membership. Re-inserting it here would just duplicate the card.
    contact.custom_attributes = {
      ...contact.custom_attributes,
      stage: toStage,
    };

    try {
      await store.dispatch('contacts/update', {
        id: contact.id,
        customAttributes: { stage: toStage },
      });
    } catch (error) {
      // Revert: vuedraggable won't undo an already-completed drag on its
      // own, so move the card back manually.
      const revertIndex = columns[toStage].contacts.findIndex(
        c => c.id === contact.id
      );
      if (revertIndex > -1) columns[toStage].contacts.splice(revertIndex, 1);
      contact.custom_attributes = {
        ...contact.custom_attributes,
        stage: fromStage,
      };
      columns[fromStage].contacts.unshift(contact);
      useAlert(t('KANBAN.ERRORS.MOVE_FAILED'));
    }
  };

  return {
    STAGES,
    VERIFICATION_STATUSES,
    PRIORITIES,
    columns,
    filters,
    searchTerm,
    sortBy,
    isSearching: computed(() => searchResultsByStage.value !== null),
    cardsForStage,
    fetchAllColumns,
    loadMore,
    runSearch,
    moveCard,
  };
}
